import React, { useCallback, useEffect, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import {
  FiActivity,
  FiBriefcase,
  FiCamera,
  FiCheck,
  FiClipboard,
  FiDownload,
  FiImage,
  FiMail,
  FiMapPin,
  FiMessageSquare,
  FiNavigation,
  FiPhone,
  FiRefreshCw,
  FiShoppingCart,
  FiTarget,
  FiUser,
  FiUserCheck,
} from "react-icons/fi";

import "react-phone-input-2/lib/style.css";
import "./sfaBookingModal.css";

const initialFormData = {
  firstName: "",
  lastName: "",
  phone: "",
  country: "in",
  company: "",
  email: "",
  requirement: "",
  captchaInput: "",
};

const generateCaptcha = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let text = "";

  for (let i = 0; i < 6; i++) {
    text += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return text;
};

const SfaBookingModal = ({ show, onClose }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [captchaText, setCaptchaText] = useState(generateCaptcha());
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) {
      return;
    }

    const bodyOverflow = document.body.style.overflow;
    const htmlOverflow = document.documentElement.style.overflow;
    const bodyPaddingRight = document.body.style.paddingRight;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.paddingRight = "0px";

    return () => {
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
      document.body.style.paddingRight = bodyPaddingRight;
    };
  }, [show]);

  const handleClose = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    setCaptchaText(generateCaptcha());
    setSubmitted(false);
    setSubmitting(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!submitted) {
      return;
    }

    const timer = setTimeout(() => {
      handleClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [submitted, handleClose]);

  const validateField = useCallback(
    (field, value) => {
      let error = "";

      const nameRegex = /^[A-Za-z][A-Za-z ._-]*$/;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

      switch (field) {
        case "firstName":
          if (!value.trim()) {
            error = "First name is required";
          } else if (!nameRegex.test(value.trim())) {
            error = "Enter a valid first name";
          } else if (value.trim().length > 50) {
            error = "Maximum 50 characters allowed";
          }
          break;

        case "lastName":
          if (!value.trim()) {
            error = "Last name is required";
          } else if (!nameRegex.test(value.trim())) {
            error = "Enter a valid last name";
          } else if (value.trim().length > 50) {
            error = "Maximum 50 characters allowed";
          }
          break;

        case "company":
          if (!value.trim()) {
            error = "Company name is required";
          } else if (value.trim().length > 100) {
            error = "Maximum 100 characters allowed";
          }
          break;

        case "email":
          if (!value.trim()) {
            error = "Email is required";
          } else if (!emailRegex.test(value.trim())) {
            error = "Enter a valid email";
          }
          break;

        case "phone": {
          const digits = value.replace(/\D/g, "");

          if (!digits) {
            error = "Phone number is required";
          } else if (digits.length < 10) {
            error = "Enter a valid mobile number";
          }

          break;
        }

        case "requirement":
          if (!value.trim()) {
            error = "Requirement is required";
          } else if (value.trim().length > 500) {
            error = "Maximum 500 characters allowed";
          }
          break;

        case "captchaInput":
          if (!value.trim()) {
            error = "Enter CAPTCHA";
          } else if (value.trim().toUpperCase() !== captchaText) {
            error = "CAPTCHA does not match";
          }
          break;

        default:
          break;
      }

      return error;
    },
    [captchaText],
  );

  const handleChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [field]: validateField(field, value),
    }));
  };

  const validateForm = () => {
    const fields = [
      "firstName",
      "lastName",
      "phone",
      "company",
      "email",
      "requirement",
      "captchaInput",
    ];

    const newErrors = {};

    fields.forEach((field) => {
      const error = validateField(field, formData[field]);

      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const response = await fetch("https://formspree.io/f/xykzrwjb", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          formType: "SFA Demo Request",
          firstName: formData.firstName,
          lastName: formData.lastName,
          name: `${formData.firstName} ${formData.lastName}`,
          contactNumber: formData.phone,
          email: formData.email,
          company: formData.company,
          requirements: formData.requirement,
          captcha: formData.captchaInput,
        }),
      });

      if (!response.ok) {
        throw new Error("Form submission failed");
      }

      setSubmitted(true);
    } catch (error) {
      console.error("SFA booking submission failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const refreshCaptcha = () => {
    setCaptchaText(generateCaptcha());

    setFormData((previous) => ({
      ...previous,
      captchaInput: "",
    }));

    setErrors((previous) => ({
      ...previous,
      captchaInput: "",
    }));
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop
      keyboard
      dialogClassName="sfa-booking-modal-dialog"
      contentClassName="sfa-booking-modal-content"
      enforceFocus
      restoreFocus
    >
      <Modal.Body className="sfa-booking-modal-body">
        <button
          type="button"
          className="sfa-modal-close"
          onClick={handleClose}
          aria-label="Close modal"
        >
          ×
        </button>

        <div className="sfa-modal-layout">
          <div className="sfa-modal-left">
            <div className="sfa-modal-left-glow sfa-modal-glow-one"></div>
            <div className="sfa-modal-left-glow sfa-modal-glow-two"></div>

            <div className="sfa-modal-left-inner">
              <div className="sfa-modal-brand">
                <span></span>
                TECHCOGNICS SFA
              </div>

              <h2>
                Smarter
                <br />
                Field Sales.
                <br />
                <span>Better Visibility.</span>
              </h2>

              <p className="sfa-modal-intro">
                See how TechCognics SFA can connect your sales representatives,
                outlets, visits, orders and field activity through one connected
                platform.
              </p>

              <div className="sfa-modal-benefits">
                <div className="sfa-modal-benefit">
                  <span>
                    <FiUserCheck />
                  </span>

                  <p>Attendance with mandatory selfie verification</p>
                </div>

                <div className="sfa-modal-benefit">
                  <span>
                    <FiMapPin />
                  </span>

                  <p>Scheduled beat visits & unscheduled field visits</p>
                </div>

                <div className="sfa-modal-benefit">
                  <span>
                    <FiTarget />
                  </span>

                  <p>100-meter radius validation for outlet check-in</p>
                </div>

                <div className="sfa-modal-benefit">
                  <span>
                    <FiNavigation />
                  </span>

                  <p>Real-time salesperson location visibility</p>
                </div>

                <div className="sfa-modal-benefit">
                  <span>
                    <FiShoppingCart />
                  </span>

                  <p>Visit execution and order capture from the field</p>
                </div>

                <div className="sfa-modal-benefit">
                  <span>
                    <FiDownload />
                  </span>

                  <p>Daily travel history available in Excel format</p>
                </div>
              </div>

              <div className="sfa-modal-flow">
                <span>Attendance</span>
                <i>→</i>
                <span>Beat</span>
                <i>→</i>
                <span>Visit</span>
                <i>→</i>
                <span>Order</span>
                <i>→</i>
                <span>Visibility</span>
              </div>

              <div className="sfa-modal-mini-dashboard">
                <div className="sfa-mini-dashboard-header">
                  <div>
                    <span>FIELD ACTIVITY</span>
                    <strong>Live Sales View</strong>
                  </div>

                  <div className="sfa-mini-live">
                    <span></span>
                    LIVE
                  </div>
                </div>

                <div className="sfa-mini-stats">
                  <div>
                    <FiMapPin />
                    <strong>24</strong>
                    <span>Visits</span>
                  </div>

                  <div>
                    <FiShoppingCart />
                    <strong>18</strong>
                    <span>Orders</span>
                  </div>

                  <div>
                    <FiActivity />
                    <strong>86%</strong>
                    <span>Execution</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="sfa-modal-right">
            <div className="sfa-modal-header">
              <span>GET STARTED</span>

              <h3>Book a Free SFA Demo</h3>

              <p>
                Tell us about your business and our SFA expert will get in touch
                with you.
              </p>
            </div>

            {submitted ? (
              <div className="sfa-thank-you">
                <div className="sfa-thank-you-icon">
                  <FiCheck />
                </div>

                <h3>Thank you for reaching out!</h3>

                <p>
                  Your SFA demo request has been received. One of our experts
                  will get in touch with you shortly.
                </p>
              </div>
            ) : (
              <div className="sfa-modal-form-scroll">
                <Form
                  id="sfaDemoForm"
                  onSubmit={handleSubmit}
                  className="sfa-booking-form"
                >
                  <div className="row g-2">
                    <div className="col-md-6">
                      <Form.Group>
                        <div
                          className={`sfa-input-group ${
                            errors.firstName ? "sfa-input-error" : ""
                          }`}
                        >
                          <span>
                            <FiUser />
                          </span>

                          <Form.Control
                            type="text"
                            placeholder="First Name"
                            autoComplete="given-name"
                            value={formData.firstName}
                            onChange={(event) =>
                              handleChange("firstName", event.target.value)
                            }
                          />
                        </div>

                        {errors.firstName && (
                          <div className="sfa-error">{errors.firstName}</div>
                        )}
                      </Form.Group>
                    </div>

                    <div className="col-md-6">
                      <Form.Group>
                        <div
                          className={`sfa-input-group ${
                            errors.lastName ? "sfa-input-error" : ""
                          }`}
                        >
                          <span>
                            <FiUser />
                          </span>

                          <Form.Control
                            type="text"
                            placeholder="Last Name"
                            autoComplete="family-name"
                            value={formData.lastName}
                            onChange={(event) =>
                              handleChange("lastName", event.target.value)
                            }
                          />
                        </div>

                        {errors.lastName && (
                          <div className="sfa-error">{errors.lastName}</div>
                        )}
                      </Form.Group>
                    </div>
                  </div>

                  <Form.Group>
                    <div
                      className={`sfa-input-group sfa-phone-group ${
                        errors.phone ? "sfa-input-error" : ""
                      }`}
                    >
                      <span className="sfa-phone-icon">
                        <FiPhone />
                      </span>

                      <PhoneInput
                        country={formData.country}
                        value={formData.phone}
                        onChange={(value, country) => {
                          setFormData((previous) => ({
                            ...previous,
                            phone: value,
                            country: country?.countryCode || "in",
                          }));

                          setErrors((previous) => ({
                            ...previous,
                            phone: validateField("phone", value),
                          }));
                        }}
                        countryCodeEditable={false}
                        enableSearch
                        specialLabel=""
                        inputProps={{
                          name: "phone",
                          autoComplete: "tel",
                          placeholder: "Phone number",
                        }}
                      />
                    </div>

                    {errors.phone && (
                      <div className="sfa-error">{errors.phone}</div>
                    )}
                  </Form.Group>

                  <Form.Group>
                    <div
                      className={`sfa-input-group ${
                        errors.company ? "sfa-input-error" : ""
                      }`}
                    >
                      <span>
                        <FiBriefcase />
                      </span>

                      <Form.Control
                        type="text"
                        placeholder="Company Name"
                        autoComplete="organization"
                        value={formData.company}
                        onChange={(event) =>
                          handleChange("company", event.target.value)
                        }
                      />
                    </div>

                    {errors.company && (
                      <div className="sfa-error">{errors.company}</div>
                    )}
                  </Form.Group>

                  <Form.Group>
                    <div
                      className={`sfa-input-group ${
                        errors.email ? "sfa-input-error" : ""
                      }`}
                    >
                      <span>
                        <FiMail />
                      </span>

                      <Form.Control
                        type="email"
                        placeholder="Business Email"
                        autoComplete="email"
                        value={formData.email}
                        onChange={(event) =>
                          handleChange("email", event.target.value)
                        }
                      />
                    </div>

                    {errors.email && (
                      <div className="sfa-error">{errors.email}</div>
                    )}
                  </Form.Group>

                  <Form.Group>
                    <div
                      className={`sfa-input-group sfa-textarea-group ${
                        errors.requirement ? "sfa-input-error" : ""
                      }`}
                    >
                      <span>
                        <FiMessageSquare />
                      </span>

                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Tell us about your sales team, field operations or SFA requirements"
                        value={formData.requirement}
                        onChange={(event) =>
                          handleChange("requirement", event.target.value)
                        }
                      />
                    </div>

                    {errors.requirement && (
                      <div className="sfa-error">{errors.requirement}</div>
                    )}
                  </Form.Group>

                  <div className="sfa-captcha-section">
                    <div className="sfa-captcha-header">
                      <span>Security Verification</span>

                      <button
                        type="button"
                        onClick={refreshCaptcha}
                        aria-label="Refresh CAPTCHA"
                      >
                        <FiRefreshCw />
                      </button>
                    </div>

                    <div className="row g-2 align-items-center">
                      <div className="col-5">
                        <div className="sfa-captcha-box">{captchaText}</div>
                      </div>

                      <div className="col-7">
                        <Form.Control
                          type="text"
                          placeholder="Enter CAPTCHA"
                          value={formData.captchaInput}
                          onChange={(event) =>
                            handleChange("captchaInput", event.target.value)
                          }
                        />
                      </div>
                    </div>

                    {errors.captchaInput && (
                      <div className="sfa-error">{errors.captchaInput}</div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="sfa-submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? "Submitting..." : "Book My SFA Demo"}

                    {!submitting && <span>→</span>}
                  </button>

                  <div className="sfa-form-note">
                    <FiClipboard />
                    <span>
                      Your information is used only to respond to your demo
                      request.
                    </span>
                  </div>
                </Form>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SfaBookingModal;
